export const algebra1Data = {
  grade: "Algebra 1",
  title: "Algebra 1 - Complete Course",
  description: "Master all Algebra 1 concepts with Mr. Augustine's easy-to-understand explanations",
  topics: [
    {
      id: "real-numbers",
      title: "The Real Number System",
      skills: [
        {
          id: "classifying-numbers",
          title: "Classifying Real Numbers",
          standards: {
            commonCore: ["HSN-RN.B.3"],
            nctm: ["Number and Operations"],
            states: {
              NY: ["AI-N.RN.3"],
              OH: ["N.RN.3"],
              PA: ["CC.2.1.HS.F.1"],
              VA: ["A.1a"],
              TX: ["A.2A"]
            }
          },
          explanation: `**Mr. Augustine says:** "Think of real numbers like a family tree! Every number belongs to one or more groups."

**The Real Number Family:**

1. **Natural Numbers (Counting Numbers):** 1, 2, 3, 4, 5...
   - These are the numbers you learned first as a child!

2. **Whole Numbers:** 0, 1, 2, 3, 4, 5...
   - Natural numbers PLUS zero

3. **Integers:** ...-3, -2, -1, 0, 1, 2, 3...
   - Whole numbers PLUS their negatives

4. **Rational Numbers:** Any number that can be written as a fraction a/b where b ≠ 0
   - Examples: 1/2, -3/4, 0.75, 0.333..., 5 (which is 5/1)
   - Includes terminating decimals (0.5) and repeating decimals (0.333...)

5. **Irrational Numbers:** Numbers that CANNOT be written as fractions
   - Examples: π, √2, √3, e
   - Their decimals go on forever WITHOUT repeating

6. **Real Numbers:** ALL of the above! (Rational + Irrational)

**Mr. Augustine's Memory Trick:**
"Natural numbers are NATURALLY what you count with. Whole numbers are the WHOLE natural family plus zero. Integers are IN THE MIDDLE (positive and negative). Rational numbers are RATIO-nal (can be written as ratios/fractions). Irrational numbers are NOT rational!"

**How to Classify:**
- Can it be written as a fraction? → Rational
- Does the decimal repeat or terminate? → Rational
- Does the decimal go on forever without repeating? → Irrational
- Is it under a square root of a non-perfect square? → Irrational`,
          practiceQuestions: [
            {
              question: "Classify the number 7 in as many ways as possible.",
              solution: `**Step 1:** Check if it's a natural number
- Yes! 7 is a counting number

**Step 2:** Check if it's a whole number
- Yes! All natural numbers are whole numbers

**Step 3:** Check if it's an integer
- Yes! All whole numbers are integers

**Step 4:** Check if it's rational
- Yes! 7 can be written as 7/1

**Step 5:** Check if it's real
- Yes! All rational numbers are real

**Answer:** 7 is natural, whole, integer, rational, and real`
            },
            {
              question: "Classify the number -5.",
              solution: `**Step 1:** Check if it's natural
- No! Natural numbers are positive only

**Step 2:** Check if it's whole
- No! Whole numbers are 0 and positive only

**Step 3:** Check if it's an integer
- Yes! -5 is a negative integer

**Step 4:** Check if it's rational
- Yes! -5 = -5/1

**Step 5:** Check if it's real
- Yes! All rational numbers are real

**Answer:** -5 is integer, rational, and real`
            },
            {
              question: "Classify the number 0.75.",
              solution: `**Step 1:** Check if it's natural
- No! 0.75 is not a counting number

**Step 2:** Check if it's whole
- No! 0.75 is not a whole number

**Step 3:** Check if it's an integer
- No! 0.75 has a decimal part

**Step 4:** Check if it's rational
- Yes! 0.75 = 75/100 = 3/4 (it's a terminating decimal)

**Step 5:** Check if it's real
- Yes! All rational numbers are real

**Answer:** 0.75 is rational and real`
            },
            {
              question: "Classify the number √16.",
              solution: `**Step 1:** Simplify first
- √16 = 4

**Step 2:** Now classify 4
- Natural? Yes!
- Whole? Yes!
- Integer? Yes!
- Rational? Yes! (4 = 4/1)
- Real? Yes!

**Answer:** √16 = 4 is natural, whole, integer, rational, and real`
            },
            {
              question: "Classify the number √7.",
              solution: `**Step 1:** Check if 7 is a perfect square
- No! √7 ≈ 2.645751311...

**Step 2:** Check the decimal
- It goes on forever without repeating

**Step 3:** This means it's irrational
- Cannot be written as a fraction

**Step 4:** Check if it's real
- Yes! All irrational numbers are real

**Answer:** √7 is irrational and real`
            },
            {
              question: "Classify the number 2/3.",
              solution: `**Step 1:** Check if it's natural
- No! 2/3 is less than 1

**Step 2:** Check if it's whole
- No! It's a fraction

**Step 3:** Check if it's an integer
- No! It's between 0 and 1

**Step 4:** Check if it's rational
- Yes! It's already written as a fraction

**Step 5:** Check if it's real
- Yes! All rational numbers are real

**Answer:** 2/3 is rational and real`
            },
            {
              question: "Classify the number π.",
              solution: `**Step 1:** Recall what π is
- π ≈ 3.14159265358979...

**Step 2:** Check the decimal
- It goes on forever without repeating

**Step 3:** This means it's irrational
- Cannot be written as a fraction

**Step 4:** Check if it's real
- Yes! All irrational numbers are real

**Answer:** π is irrational and real`
            },
            {
              question: "Classify the number 0.",
              solution: `**Step 1:** Check if it's natural
- No! Natural numbers start at 1

**Step 2:** Check if it's whole
- Yes! 0 is the first whole number

**Step 3:** Check if it's an integer
- Yes! All whole numbers are integers

**Step 4:** Check if it's rational
- Yes! 0 = 0/1

**Step 5:** Check if it's real
- Yes! All rational numbers are real

**Answer:** 0 is whole, integer, rational, and real`
            },
            {
              question: "Classify the number -√25.",
              solution: `**Step 1:** Simplify first
- √25 = 5, so -√25 = -5

**Step 2:** Now classify -5
- Natural? No!
- Whole? No!
- Integer? Yes!
- Rational? Yes! (-5 = -5/1)
- Real? Yes!

**Answer:** -√25 = -5 is integer, rational, and real`
            },
            {
              question: "Classify the number 0.333... (repeating).",
              solution: `**Step 1:** Recognize the pattern
- 0.333... = 0.3̄ (the bar means repeating)

**Step 2:** Check if it's rational
- Yes! Repeating decimals are rational
- 0.333... = 1/3

**Step 3:** Check other categories
- Natural? No!
- Whole? No!
- Integer? No!

**Step 4:** Check if it's real
- Yes! All rational numbers are real

**Answer:** 0.333... is rational and real`
            },
            {
              question: "Classify the number √2.",
              solution: `**Step 1:** Check if 2 is a perfect square
- No! √2 ≈ 1.414213562...

**Step 2:** Check the decimal
- It goes on forever without repeating

**Step 3:** This means it's irrational
- Cannot be written as a fraction

**Step 4:** Check if it's real
- Yes! All irrational numbers are real

**Answer:** √2 is irrational and real`
            },
            {
              question: "Classify the number -8/4.",
              solution: `**Step 1:** Simplify first
- -8/4 = -2

**Step 2:** Now classify -2
- Natural? No!
- Whole? No!
- Integer? Yes!
- Rational? Yes! (-2 = -2/1)
- Real? Yes!

**Answer:** -8/4 = -2 is integer, rational, and real`
            },
            {
              question: "Classify the number 5.0.",
              solution: `**Step 1:** Recognize that 5.0 = 5
- The decimal doesn't change the value

**Step 2:** Classify 5
- Natural? Yes!
- Whole? Yes!
- Integer? Yes!
- Rational? Yes! (5 = 5/1)
- Real? Yes!

**Answer:** 5.0 = 5 is natural, whole, integer, rational, and real`
            },
            {
              question: "Classify the number √-4.",
              solution: `**Step 1:** Check if we can take the square root of a negative
- No! Square roots of negative numbers are NOT real

**Step 2:** This is an imaginary number
- √-4 = 2i (where i = √-1)

**Step 3:** Check if it's real
- No! Imaginary numbers are not part of the real number system

**Answer:** √-4 is NOT a real number (it's imaginary)`
            },
            {
              question: "Classify the number 0.125.",
              solution: `**Step 1:** Check if it's a terminating decimal
- Yes! It ends after 3 decimal places

**Step 2:** Convert to a fraction
- 0.125 = 125/1000 = 1/8

**Step 3:** Check if it's rational
- Yes! It can be written as a fraction

**Step 4:** Check other categories
- Natural? No!
- Whole? No!
- Integer? No!

**Step 5:** Check if it's real
- Yes! All rational numbers are real

**Answer:** 0.125 is rational and real`
            },
            {
              question: "Classify the number -√9.",
              solution: `**Step 1:** Simplify first
- √9 = 3, so -√9 = -3

**Step 2:** Now classify -3
- Natural? No!
- Whole? No!
- Integer? Yes!
- Rational? Yes! (-3 = -3/1)
- Real? Yes!

**Answer:** -√9 = -3 is integer, rational, and real`
            },
            {
              question: "Classify the number 22/7.",
              solution: `**Step 1:** Check if it's already a fraction
- Yes! 22/7 is in fraction form

**Step 2:** Check if it's rational
- Yes! It's written as a fraction

**Step 3:** Check other categories
- Natural? No! (22/7 ≈ 3.14, not a whole number)
- Whole? No!
- Integer? No!

**Step 4:** Check if it's real
- Yes! All rational numbers are real

**Note:** 22/7 is often used as an approximation for π, but 22/7 is rational while π is irrational!

**Answer:** 22/7 is rational and real`
            },
            {
              question: "Classify the number √100.",
              solution: `**Step 1:** Simplify first
- √100 = 10

**Step 2:** Now classify 10
- Natural? Yes!
- Whole? Yes!
- Integer? Yes!
- Rational? Yes! (10 = 10/1)
- Real? Yes!

**Answer:** √100 = 10 is natural, whole, integer, rational, and real`
            },
            {
              question: "Classify the number -0.5.",
              solution: `**Step 1:** Convert to a fraction
- -0.5 = -1/2

**Step 2:** Check if it's rational
- Yes! It can be written as a fraction

**Step 3:** Check other categories
- Natural? No!
- Whole? No!
- Integer? No!

**Step 4:** Check if it's real
- Yes! All rational numbers are real

**Answer:** -0.5 is rational and real`
            },
            {
              question: "Classify the number √50.",
              solution: `**Step 1:** Check if 50 is a perfect square
- No! √50 ≈ 7.071067812...

**Step 2:** Check the decimal
- It goes on forever without repeating

**Step 3:** This means it's irrational
- Cannot be written as a fraction

**Step 4:** Check if it's real
- Yes! All irrational numbers are real

**Answer:** √50 is irrational and real`
            }
          ]
        },
        {
          id: "properties-real-numbers",
          title: "Properties of Real Numbers",
          standards: {
            commonCore: ["HSA-REI.A.1"],
            nctm: ["Algebra"],
            states: {
              NY: ["AI-A.REI.1"],
              OH: ["A.REI.1"],
              PA: ["CC.2.2.HS.D.1"],
              VA: ["A.2a"],
              TX: ["A.5A"]
            }
          },
          explanation: `**Mr. Augustine says:** "Properties are like the 'rules of the game' for numbers. Once you know them, math becomes SO much easier!"

**The Big 5 Properties:**

**1. Commutative Property (Order doesn't matter)**
- **Addition:** a + b = b + a
  - Example: 3 + 5 = 5 + 3 (both equal 8)
- **Multiplication:** a × b = b × a
  - Example: 4 × 7 = 7 × 4 (both equal 28)
- **Mr. Augustine's Tip:** "Commute means to travel. Numbers can travel (switch places) in addition and multiplication!"
- **NOTE:** Subtraction and division are NOT commutative!

**2. Associative Property (Grouping doesn't matter)**
- **Addition:** (a + b) + c = a + (b + c)
  - Example: (2 + 3) + 4 = 2 + (3 + 4) (both equal 9)
- **Multiplication:** (a × b) × c = a × (b × c)
  - Example: (2 × 3) × 4 = 2 × (3 × 4) (both equal 24)
- **Mr. Augustine's Tip:** "Associate means to group together. You can group numbers differently in addition and multiplication!"

**3. Distributive Property (Multiply through parentheses)**
- **Formula:** a(b + c) = ab + ac
  - Example: 3(x + 5) = 3x + 15
- **Also works with subtraction:** a(b - c) = ab - ac
  - Example: 4(x - 2) = 4x - 8
- **Mr. Augustine's Tip:** "Distribute means to hand out. The number outside hands itself out to everything inside!"

**4. Identity Properties (Special numbers that don't change things)**
- **Additive Identity:** a + 0 = a
  - Example: 7 + 0 = 7
  - "Zero is the identity for addition!"
- **Multiplicative Identity:** a × 1 = a
  - Example: 9 × 1 = 9
  - "One is the identity for multiplication!"

**5. Inverse Properties (Opposites that cancel out)**
- **Additive Inverse:** a + (-a) = 0
  - Example: 5 + (-5) = 0
  - "Every number has an opposite that adds to zero!"
- **Multiplicative Inverse:** a × (1/a) = 1 (where a ≠ 0)
  - Example: 4 × (1/4) = 1
  - "Every number (except 0) has a reciprocal that multiplies to one!"

**How to Use These:**
1. Simplifying expressions
2. Solving equations
3. Mental math shortcuts
4. Proving mathematical statements`,
          practiceQuestions: [
            {
              question: "Which property is shown: 5 + 8 = 8 + 5?",
              solution: `**Step 1:** Look at what changed
- The order of the numbers switched

**Step 2:** Identify the operation
- Addition

**Step 3:** Recall the property
- When order doesn't matter in addition, it's the Commutative Property

**Answer:** Commutative Property of Addition`
            },
            {
              question: "Which property is shown: 3(x + 4) = 3x + 12?",
              solution: `**Step 1:** Look at what happened
- The 3 was multiplied by both terms inside the parentheses

**Step 2:** Identify the operation
- Multiplication was distributed over addition

**Step 3:** Recall the property
- When you multiply through parentheses, it's the Distributive Property

**Answer:** Distributive Property`
            },
            {
              question: "Which property is shown: (2 × 5) × 7 = 2 × (5 × 7)?",
              solution: `**Step 1:** Look at what changed
- The grouping (parentheses) changed

**Step 2:** Identify the operation
- Multiplication

**Step 3:** Recall the property
- When grouping doesn't matter in multiplication, it's the Associative Property

**Answer:** Associative Property of Multiplication`
            },
            {
              question: "Simplify using the Distributive Property: 5(x + 3)",
              solution: `**Step 1:** Identify what to distribute
- Distribute the 5 to both terms inside

**Step 2:** Multiply 5 by the first term
- 5 × x = 5x

**Step 3:** Multiply 5 by the second term
- 5 × 3 = 15

**Step 4:** Write the result
- 5x + 15

**Answer:** 5x + 15`
            },
            {
              question: "Which property is shown: 9 + 0 = 9?",
              solution: `**Step 1:** Look at what happened
- Adding 0 didn't change the number

**Step 2:** Identify the special number
- 0 is the additive identity

**Step 3:** Recall the property
- This is the Identity Property of Addition

**Answer:** Identity Property of Addition (or Additive Identity)`
            },
            {
              question: "Which property is shown: 7 × 1 = 7?",
              solution: `**Step 1:** Look at what happened
- Multiplying by 1 didn't change the number

**Step 2:** Identify the special number
- 1 is the multiplicative identity

**Step 3:** Recall the property
- This is the Identity Property of Multiplication

**Answer:** Identity Property of Multiplication (or Multiplicative Identity)`
            },
            {
              question: "Simplify using the Distributive Property: -2(x - 5)",
              solution: `**Step 1:** Identify what to distribute
- Distribute the -2 to both terms inside

**Step 2:** Multiply -2 by the first term
- -2 × x = -2x

**Step 3:** Multiply -2 by the second term
- -2 × (-5) = +10

**Step 4:** Write the result
- -2x + 10

**Answer:** -2x + 10`
            },
            {
              question: "Which property is shown: 6 + (-6) = 0?",
              solution: `**Step 1:** Look at what happened
- A number and its opposite added to zero

**Step 2:** Identify the relationship
- 6 and -6 are additive inverses

**Step 3:** Recall the property
- This is the Inverse Property of Addition

**Answer:** Inverse Property of Addition (or Additive Inverse)`
            },
            {
              question: "Which property is shown: 8 × (1/8) = 1?",
              solution: `**Step 1:** Look at what happened
- A number and its reciprocal multiplied to one

**Step 2:** Identify the relationship
- 8 and 1/8 are multiplicative inverses

**Step 3:** Recall the property
- This is the Inverse Property of Multiplication

**Answer:** Inverse Property of Multiplication (or Multiplicative Inverse)`
            },
            {
              question: "Use the Commutative Property to rewrite: 4x + 7",
              solution: `**Step 1:** Identify the operation
- Addition

**Step 2:** Apply the Commutative Property
- Switch the order of the terms

**Step 3:** Write the result
- 7 + 4x

**Answer:** 7 + 4x`
            },
            {
              question: "Simplify: 3(2x + 5) using the Distributive Property",
              solution: `**Step 1:** Distribute 3 to the first term
- 3 × 2x = 6x

**Step 2:** Distribute 3 to the second term
- 3 × 5 = 15

**Step 3:** Write the result
- 6x + 15

**Answer:** 6x + 15`
            },
            {
              question: "Which property is shown: (x + 3) + 5 = x + (3 + 5)?",
              solution: `**Step 1:** Look at what changed
- The grouping (parentheses) changed

**Step 2:** Identify the operation
- Addition

**Step 3:** Recall the property
- When grouping doesn't matter in addition, it's the Associative Property

**Answer:** Associative Property of Addition`
            },
            {
              question: "Simplify: 4(x - 3) + 2x",
              solution: `**Step 1:** Distribute 4 to both terms
- 4 × x = 4x
- 4 × (-3) = -12
- Result: 4x - 12

**Step 2:** Combine with the remaining term
- 4x - 12 + 2x

**Step 3:** Combine like terms
- 4x + 2x = 6x
- Result: 6x - 12

**Answer:** 6x - 12`
            },
            {
              question: "Name the property: 5(xy) = (5x)y",
              solution: `**Step 1:** Look at what changed
- The grouping changed

**Step 2:** Identify the operation
- Multiplication

**Step 3:** Recall the property
- When grouping doesn't matter in multiplication, it's the Associative Property

**Answer:** Associative Property of Multiplication`
            },
            {
              question: "Simplify: -3(2x - 4) + 5x",
              solution: `**Step 1:** Distribute -3 to both terms
- -3 × 2x = -6x
- -3 × (-4) = +12
- Result: -6x + 12

**Step 2:** Combine with the remaining term
- -6x + 12 + 5x

**Step 3:** Combine like terms
- -6x + 5x = -x
- Result: -x + 12

**Answer:** -x + 12 (or 12 - x)`
            },
            {
              question: "Which property justifies this step: 2x + 0 = 2x?",
              solution: `**Step 1:** Look at what happened
- Adding 0 didn't change the expression

**Step 2:** Identify the special number
- 0 is the additive identity

**Step 3:** Recall the property
- This is the Identity Property of Addition

**Answer:** Identity Property of Addition`
            },
            {
              question: "Simplify: 5(x + 2) - 3(x - 1)",
              solution: `**Step 1:** Distribute 5 to both terms in the first parentheses
- 5 × x = 5x
- 5 × 2 = 10
- Result: 5x + 10

**Step 2:** Distribute -3 to both terms in the second parentheses
- -3 × x = -3x
- -3 × (-1) = +3
- Result: -3x + 3

**Step 3:** Combine all terms
- 5x + 10 - 3x + 3

**Step 4:** Combine like terms
- 5x - 3x = 2x
- 10 + 3 = 13
- Result: 2x + 13

**Answer:** 2x + 13`
            },
            {
              question: "Name the property: ab = ba",
              solution: `**Step 1:** Look at what changed
- The order of the variables switched

**Step 2:** Identify the operation
- Multiplication

**Step 3:** Recall the property
- When order doesn't matter in multiplication, it's the Commutative Property

**Answer:** Commutative Property of Multiplication`
            },
            {
              question: "Which property justifies: x · 1 = x?",
              solution: `**Step 1:** Look at what happened
- Multiplying by 1 didn't change the variable

**Step 2:** Identify the special number
- 1 is the multiplicative identity

**Step 3:** Recall the property
- This is the Identity Property of Multiplication

**Answer:** Identity Property of Multiplication`
            },
            {
              question: "Simplify: 2(3x + 4) + 3(x - 2)",
              solution: `**Step 1:** Distribute 2 to both terms in the first parentheses
- 2 × 3x = 6x
- 2 × 4 = 8
- Result: 6x + 8

**Step 2:** Distribute 3 to both terms in the second parentheses
- 3 × x = 3x
- 3 × (-2) = -6
- Result: 3x - 6

**Step 3:** Combine all terms
- 6x + 8 + 3x - 6

**Step 4:** Combine like terms
- 6x + 3x = 9x
- 8 - 6 = 2
- Result: 9x + 2

**Answer:** 9x + 2`
            }
          ]
        }
      ]
    },
    {
      id: "expressions-variables",
      title: "Expressions & Variables",
      skills: [
        {
          id: "writing-expressions",
          title: "Writing Algebraic Expressions",
          standards: {
            commonCore: ["HSA-SSE.A.1"],
            nctm: ["Algebra"],
            states: {
              NY: ["AI-A.SSE.1"],
              OH: ["A.SSE.1"],
              PA: ["CC.2.2.HS.D.2"],
              VA: ["A.4a"],
              TX: ["A.2C"]
            }
          },
          explanation: `**Mr. Augustine says:** "Translating words into algebra is like learning a new language. Once you know the key words, it's easy!"

**Key Translation Words:**

**Addition (+):**
- sum, plus, more than, increased by, total, added to
- Example: "5 more than x" → x + 5

**Subtraction (-):**
- difference, minus, less than, decreased by, subtracted from
- Example: "7 less than x" → x - 7
- **CAREFUL:** "less than" reverses the order!

**Multiplication (×):**
- product, times, of, multiplied by
- Example: "the product of 3 and x" → 3x
- **NOTE:** We usually don't write the × symbol in algebra!

**Division (÷):**
- quotient, divided by, ratio, per
- Example: "x divided by 4" → x/4 or x ÷ 4

**Mr. Augustine's Translation Tips:**

1. **"More than" and "less than" REVERSE the order!**
   - "5 more than x" → x + 5 (NOT 5 + x, though they're equal)
   - "5 less than x" → x - 5 (NOT 5 - x, these are different!)

2. **"Of" usually means multiply**
   - "half of x" → (1/2)x or x/2
   - "20% of x" → 0.20x

3. **Look for the variable first**
   - Identify what the unknown is
   - Then build the expression around it

4. **Watch for order!**
   - "x divided by 5" → x/5
   - "5 divided by x" → 5/x (different!)

**Common Patterns:**
- "twice a number" → 2x
- "a number squared" → x²
- "the sum of a number and 5" → x + 5
- "the product of a number and 3" → 3x
- "5 less than twice a number" → 2x - 5`,
          practiceQuestions: [
            {
              question: "Write an expression for: The sum of x and 8",
              solution: `**Step 1:** Identify the key word
- "sum" means addition

**Step 2:** Identify the terms
- x and 8

**Step 3:** Write the expression
- x + 8

**Answer:** x + 8`
            },
            {
              question: "Write an expression for: 5 less than x",
              solution: `**Step 1:** Identify the key phrase
- "less than" means subtraction AND reverses order

**Step 2:** Set up the subtraction
- Start with x
- Subtract 5

**Step 3:** Write the expression
- x - 5

**Mr. Augustine's Warning:** NOT 5 - x! "Less than" reverses the order!

**Answer:** x - 5`
            },
            {
              question: "Write an expression for: The product of 7 and y",
              solution: `**Step 1:** Identify the key word
- "product" means multiplication

**Step 2:** Identify the factors
- 7 and y

**Step 3:** Write the expression
- 7y (we don't write the × symbol)

**Answer:** 7y`
            },
            {
              question: "Write an expression for: x divided by 3",
              solution: `**Step 1:** Identify the key phrase
- "divided by" means division

**Step 2:** Set up the division
- x is being divided
- 3 is the divisor

**Step 3:** Write the expression
- x/3 or x ÷ 3

**Answer:** x/3`
            },
            {
              question: "Write an expression for: 4 more than twice a number",
              solution: `**Step 1:** Break it into parts
- "twice a number" → 2x
- "4 more than" → add 4

**Step 2:** Combine the parts
- Start with 2x
- Add 4

**Step 3:** Write the expression
- 2x + 4

**Answer:** 2x + 4`
            },
            {
              question: "Write an expression for: The difference between x and 9",
              solution: `**Step 1:** Identify the key word
- "difference" means subtraction

**Step 2:** Determine the order
- "difference between x and 9" means x - 9

**Step 3:** Write the expression
- x - 9

**Answer:** x - 9`
            },
            {
              question: "Write an expression for: Half of a number",
              solution: `**Step 1:** Identify the key phrase
- "half of" means multiply by 1/2 or divide by 2

**Step 2:** Set up the expression
- (1/2)x or x/2

**Step 3:** Choose the clearest form
- Both are correct!

**Answer:** x/2 or (1/2)x`
            },
            {
              question: "Write an expression for: 3 less than the product of 5 and x",
              solution: `**Step 1:** Break it into parts
- "product of 5 and x" → 5x
- "3 less than" → subtract 3

**Step 2:** Combine the parts
- Start with 5x
- Subtract 3

**Step 3:** Write the expression
- 5x - 3

**Answer:** 5x - 3`
            },
            {
              question: "Write an expression for: The quotient of x and 4, increased by 7",
              solution: `**Step 1:** Break it into parts
- "quotient of x and 4" → x/4
- "increased by 7" → add 7

**Step 2:** Combine the parts
- Start with x/4
- Add 7

**Step 3:** Write the expression
- x/4 + 7

**Answer:** x/4 + 7`
            },
            {
              question: "Write an expression for: A number squared, minus 10",
              solution: `**Step 1:** Break it into parts
- "a number squared" → x²
- "minus 10" → subtract 10

**Step 2:** Combine the parts
- Start with x²
- Subtract 10

**Step 3:** Write the expression
- x² - 10

**Answer:** x² - 10`
            },
            {
              question: "Write an expression for: 8 more than the quotient of x and 2",
              solution: `**Step 1:** Break it into parts
- "quotient of x and 2" → x/2
- "8 more than" → add 8

**Step 2:** Combine the parts
- Start with x/2
- Add 8

**Step 3:** Write the expression
- x/2 + 8

**Answer:** x/2 + 8`
            },
            {
              question: "Write an expression for: The product of 6 and the sum of x and 3",
              solution: `**Step 1:** Break it into parts
- "sum of x and 3" → (x + 3)
- "product of 6 and..." → multiply by 6

**Step 2:** Combine the parts
- 6 times (x + 3)
- Need parentheses!

**Step 3:** Write the expression
- 6(x + 3)

**Answer:** 6(x + 3)`
            },
            {
              question: "Write an expression for: 5 decreased by twice a number",
              solution: `**Step 1:** Break it into parts
- "twice a number" → 2x
- "5 decreased by" → 5 minus...

**Step 2:** Set up the subtraction
- Start with 5
- Subtract 2x

**Step 3:** Write the expression
- 5 - 2x

**Answer:** 5 - 2x`
            },
            {
              question: "Write an expression for: The sum of x and y, divided by 3",
              solution: `**Step 1:** Break it into parts
- "sum of x and y" → (x + y)
- "divided by 3" → divide by 3

**Step 2:** Combine the parts
- (x + y) divided by 3
- Need parentheses!

**Step 3:** Write the expression
- (x + y)/3

**Answer:** (x + y)/3`
            },
            {
              question: "Write an expression for: 10 less than the product of 4 and x",
              solution: `**Step 1:** Break it into parts
- "product of 4 and x" → 4x
- "10 less than" → subtract 10

**Step 2:** Combine the parts
- Start with 4x
- Subtract 10

**Step 3:** Write the expression
- 4x - 10

**Answer:** 4x - 10`
            },
            {
              question: "Write an expression for: The square of the sum of x and 5",
              solution: `**Step 1:** Break it into parts
- "sum of x and 5" → (x + 5)
- "square of..." → raise to the power of 2

**Step 2:** Combine the parts
- (x + 5) squared
- Need parentheses!

**Step 3:** Write the expression
- (x + 5)²

**Answer:** (x + 5)²`
            },
            {
              question: "Write an expression for: One-third of the difference between x and 7",
              solution: `**Step 1:** Break it into parts
- "difference between x and 7" → (x - 7)
- "one-third of..." → multiply by 1/3

**Step 2:** Combine the parts
- (1/3) times (x - 7)
- Need parentheses!

**Step 3:** Write the expression
- (1/3)(x - 7) or (x - 7)/3

**Answer:** (x - 7)/3 or (1/3)(x - 7)`
            },
            {
              question: "Write an expression for: 20% of x, increased by 5",
              solution: `**Step 1:** Break it into parts
- "20% of x" → 0.20x or 0.2x
- "increased by 5" → add 5

**Step 2:** Combine the parts
- Start with 0.2x
- Add 5

**Step 3:** Write the expression
- 0.2x + 5

**Answer:** 0.2x + 5`
            },
            {
              question: "Write an expression for: The product of x and y, decreased by 8",
              solution: `**Step 1:** Break it into parts
- "product of x and y" → xy
- "decreased by 8" → subtract 8

**Step 2:** Combine the parts
- Start with xy
- Subtract 8

**Step 3:** Write the expression
- xy - 8

**Answer:** xy - 8`
            },
            {
              question: "Write an expression for: 7 more than the quotient of x and the sum of y and 3",
              solution: `**Step 1:** Break it into parts (inside out)
- "sum of y and 3" → (y + 3)
- "quotient of x and..." → x/(y + 3)
- "7 more than..." → add 7

**Step 2:** Combine the parts
- Start with x/(y + 3)
- Add 7

**Step 3:** Write the expression
- x/(y + 3) + 7

**Answer:** x/(y + 3) + 7`
            }
          ]
        }
      ]
    }
  ]
};